/**
 * Collection routines: unified draggable, infinite carousel.
 *
 * Desktop (>=768px): 3 items visible, middle is centered/active.
 * Mobile (<768px):   single item visible with neighbor peeks.
 *
 * Both use clones for infinite loop (when n >= 3) and pixel-based
 * centering so drag/swipe works the same everywhere.
 *
 * Track layout with clones: [C_{n-2}, C_{n-1}, R_0, ..., R_{n-1}, C_0, C_1]
 * trackIndex points at the currently centered item in allItems.
 */
(function () {
  var MOBILE_BP = 768;
  var MAGNET_RADIUS = 110;
  var MAGNET_STRENGTH = 0.28;
  var AXIS_LOCK_THRESHOLD = 6;
  var DRAG_CLICK_SUPPRESS_THRESHOLD = 8;
  var CLICK_SUPPRESS_MS = 400;

  function isMobile() {
    return window.innerWidth < MOBILE_BP;
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  function initRoutines(root) {
    var allItems = Array.from(root.querySelectorAll('[data-routines-item]'));
    var realItems = allItems.filter(function (el) {
      return !el.hasAttribute('data-routines-clone');
    });
    var track = root.querySelector('[data-routines-track]');
    var cta = root.querySelector('[data-routines-cta]');
    var prevBtn = root.querySelector('[data-routines-prev]');
    var nextBtn = root.querySelector('[data-routines-next]');
    var mobileTab = root.querySelector('[data-routines-mobile-tab]');
    var n = realItems.length;
    if (!n || !track) return;

    var hasLoop = allItems.length > n;
    var realStart = hasLoop ? 2 : 0;
    var realEnd = hasLoop ? n + 1 : n - 1;
    var trackIndex = realStart;
    var transitioning = false;
    var suppressClick = false;

    /* ---------- helpers ---------- */

    function getRealIdx(ti) {
      var el = allItems[ti];
      return el ? parseInt(el.getAttribute('data-real-index'), 10) || 0 : 0;
    }

    function computeTranslateFor(ti) {
      var item = allItems[ti];
      if (!item) return 0;
      var containerW = track.parentElement.clientWidth;
      return -(item.offsetLeft - (containerW - item.offsetWidth) / 2);
    }

    function setTransform(x, animated) {
      if (!animated) {
        track.style.transition = 'none';
        track.style.transform = 'translate3d(' + x + 'px, 0, 0)';
        track.offsetHeight;
        track.style.transition = '';
      } else {
        track.style.transition = '';
        track.style.transform = 'translate3d(' + x + 'px, 0, 0)';
      }
    }

    function slideTo(ti, animated) {
      setTransform(computeTranslateFor(ti), animated);
    }

    /* ---------- CTA label: preserve buttons.js structure ---------- */

    function setCtaTarget(link, label) {
      if (!cta) return;
      if (!link) {
        cta.classList.add('hidden');
        return;
      }
      cta.classList.remove('hidden');
      cta.href = link;

      var inner = cta.querySelector('.btn__text span[data-text]');
      if (inner) {
        inner.setAttribute('data-text', label);
        inner.textContent = label;
      } else {
        cta.textContent = label;
      }
    }

    /* ---------- visuals ---------- */

    function updateVisuals(activeRI) {
      allItems.forEach(function (item) {
        var ri = parseInt(item.getAttribute('data-real-index'), 10);
        var isActive = ri === activeRI;
        item.setAttribute('data-routines-state', isActive ? 'active' : 'inactive');
        var tab = item.querySelector('[data-routines-tab]');
        if (tab) {
          tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
          tab.setAttribute('tabindex', isActive ? '0' : '-1');
        }
      });

      if (mobileTab) {
        var mi = realItems[activeRI];
        if (mi) mobileTab.textContent = mi.getAttribute('data-tab-title') || 'Collection';
      }

      var cur = realItems[activeRI];
      if (cur) {
        setCtaTarget(cur.getAttribute('data-link'), cur.getAttribute('data-button-label') || 'Shop now');
      }
    }

    function update(animated) {
      updateVisuals(getRealIdx(trackIndex));
      slideTo(trackIndex, animated !== false);
    }

    /* ---------- navigation ---------- */

    function shiftBy(steps) {
      if (transitioning || steps === 0) return;
      if (hasLoop) {
        transitioning = true;
        trackIndex += steps;
        update(true);
      } else {
        trackIndex = Math.max(0, Math.min(realEnd, trackIndex + steps));
        update(true);
      }
    }

    function shiftNext() { shiftBy(1); }
    function shiftPrev() { shiftBy(-1); }

    /* ---------- infinite-loop snap ---------- */

    if (hasLoop) {
      track.addEventListener('transitionend', function (e) {
        if (e.target !== track || e.propertyName !== 'transform') return;
        transitioning = false;
        var needsSnap = false;
        while (trackIndex < realStart) { trackIndex += n; needsSnap = true; }
        while (trackIndex > realEnd)   { trackIndex -= n; needsSnap = true; }
        if (needsSnap) slideTo(trackIndex, false);
      });
    }

    /* ---------- tab + nav clicks ---------- */

    allItems.forEach(function (item) {
      var tab = item.querySelector('[data-routines-tab]');
      if (!tab) return;
      tab.addEventListener('click', function (e) {
        if (suppressClick) { e.preventDefault(); return; }
        if (transitioning) return;
        var targetRI = parseInt(item.getAttribute('data-real-index'), 10);
        var currentRI = getRealIdx(trackIndex);
        var diff = targetRI - currentRI;
        if (hasLoop) {
          while (diff > n / 2)  diff -= n;
          while (diff < -n / 2) diff += n;
        }
        if (diff !== 0) shiftBy(diff);
      });
    });

    if (prevBtn) prevBtn.addEventListener('click', function (e) {
      if (suppressClick) { e.preventDefault(); return; }
      shiftPrev();
    });
    if (nextBtn) nextBtn.addEventListener('click', function (e) {
      if (suppressClick) { e.preventDefault(); return; }
      shiftNext();
    });

    /* ---------- keyboard ---------- */

    root.addEventListener('keydown', function (e) {
      var tab = e.target.closest('[data-routines-tab]');
      if (!tab || !root.contains(tab)) return;
      if (e.key === 'ArrowRight') { shiftNext(); e.preventDefault(); }
      else if (e.key === 'ArrowLeft') { shiftPrev(); e.preventDefault(); }
    });

    /* ---------- pointer drag ---------- */

    var dragging = false;
    var startX = 0;
    var startY = 0;
    var baseline = 0;
    var delta = 0;
    var axis = null;

    track.addEventListener('dragstart', function (e) { e.preventDefault(); });

    track.addEventListener('pointerdown', function (e) {
      if (transitioning) return;
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      if (e.target.closest('[data-routines-tab]')) return;
      dragging = true;
      startX = e.clientX;
      startY = e.clientY;
      delta = 0;
      axis = null;
      baseline = computeTranslateFor(trackIndex);
      track.classList.add('is-dragging');
    });

    window.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - startX;
      var dy = e.clientY - startY;
      if (!axis) {
        if (Math.abs(dx) < AXIS_LOCK_THRESHOLD && Math.abs(dy) < AXIS_LOCK_THRESHOLD) return;
        axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      }
      if (axis === 'y') {
        dragging = false;
        track.classList.remove('is-dragging');
        setTransform(baseline, true);
        return;
      }
      if (e.cancelable) e.preventDefault();
      delta = dx;
      setTransform(baseline + dx, false);
    }, { passive: false });

    function endDrag() {
      if (!dragging) return;
      dragging = false;
      track.classList.remove('is-dragging');

      if (Math.abs(delta) > DRAG_CLICK_SUPPRESS_THRESHOLD) {
        suppressClick = true;
        setTimeout(function () { suppressClick = false; }, CLICK_SUPPRESS_MS);
      }

      var item = allItems[trackIndex];
      var itemW = item ? item.offsetWidth : 1;
      var steps = Math.round(-delta / itemW);

      if (!hasLoop) {
        var target = Math.max(0, Math.min(realEnd, trackIndex + steps));
        steps = target - trackIndex;
      }

      if (steps === 0) slideTo(trackIndex, true);
      else shiftBy(steps);
    }

    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);

    /* ---------- click suppression after drag ---------- */

    track.addEventListener('click', function (e) {
      if (suppressClick) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    }, true);

    /* ---------- magnetic nav arrows ---------- */

    function bindMagnet(btn) {
      if (!btn || prefersReducedMotion()) return;
      var rect = null;
      function refresh() { rect = btn.getBoundingClientRect(); }
      window.addEventListener('scroll', function () { rect = null; }, { passive: true });

      btn.addEventListener('pointerenter', refresh);
      btn.addEventListener('pointermove', function (e) {
        if (isMobile()) return;
        if (!rect) refresh();
        var cx = rect.left + rect.width / 2;
        var cy = rect.top + rect.height / 2;
        var dx = e.clientX - cx;
        var dy = e.clientY - cy;
        var dist = Math.hypot(dx, dy);
        if (dist > MAGNET_RADIUS) return;
        btn.style.setProperty('--magnet-x', (dx * MAGNET_STRENGTH).toFixed(2) + 'px');
        btn.style.setProperty('--magnet-y', (dy * MAGNET_STRENGTH).toFixed(2) + 'px');
      });
      btn.addEventListener('pointerleave', function () {
        btn.style.setProperty('--magnet-x', '0px');
        btn.style.setProperty('--magnet-y', '0px');
      });
    }

    bindMagnet(prevBtn);
    bindMagnet(nextBtn);

    /* ---------- init ---------- */

    update(false);

    /* ---------- resize ---------- */

    var resizeTimer;
    window.addEventListener('resize', function () {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(function () { update(false); }, 100);
    }, { passive: true });
  }

  /* ---------- boot ---------- */

  function boot() {
    document.querySelectorAll('[data-collection-routines]').forEach(initRoutines);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  document.addEventListener('shopify:section:load', function (e) {
    e.target.querySelectorAll('[data-collection-routines]').forEach(initRoutines);
  });
})();
